import React, { useEffect, useState } from "react";
import {
  ServiceScopeData,
  usePartnerOnboarding,
} from "../../contexts/PartnerOnboardingContext";
import NavigationButton from "../components/BrandsPartnerComponents/navigation_button";
import { GetRegionsList } from "../../API/endpoints/Regions";
import { PartnerMarketType, PartnerType } from "./dtos/partner.dtos";
import MarketSelection from "../components/BrandsPartnerComponents/MarketSelection";
interface OptionProps {
  id: number;
  code: string;
  name: string;
}
interface Props {
  previousStep: () => void;
  nextStep: () => void;
  data: PartnerType;
  setData: React.Dispatch<React.SetStateAction<PartnerType>>;
}

const PickMarketsPage: React.FC<Props> = ({
  previousStep,
  nextStep,
  data,
  setData,
}) => {
  useEffect(() => {
    (async () => {
      try {
        const response = await GetRegionsList();
        setAllMarkets(response.data.map((item: any) => item));
      } catch (error) {}
    })();
  }, []);

  const { selectedMarkets, setSelectedMarkets, localMarkets, setLocalMarkets } =
    usePartnerOnboarding();

  // Local state for selection within this page
  const [otherInputValue, setOtherInputValue] = React.useState("");
  const [allMarkets, setAllMarkets] = useState<any[]>([]);
  // Track validation error
  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    if (data.markets && allMarkets.length > 0) {
      const restored = Object.keys(data.markets)
        .map((marketName) => {
          const matched = allMarkets.find((m) => m.name === marketName);
          return matched ? { id: matched.id, name: matched.name } : null;
        })
        .filter((item): item is OptionProps => !!item);

      setLocalMarkets(restored);
    }
  }, [allMarkets, data.markets]);

  const handleNext = () => {
    if (localMarkets.length === 0) {
      setValidationError("Please select at least one market.");
      return; // Stop here if validation fails
    }
    const defaultScope: ServiceScopeData = {
      healthSupplements: [],
      beautyAndSkin: [],
      foodAndBeverages: [],
      petNutrition: [],
      keyChannelCoverage: [],
      keyCapabilities: [],
      warehousing: [],
      acceptedBusinessModels: [],
      tradingTerms: [],
      minimumExclusivityTenure: [],
      preferenceonMarketingInvestment: [],
      paymentTerms: [],
    };

    const updatedMarkets = localMarkets.map((item) => {
      // Check if this market already has a serviceScope defined in context
      const existing = selectedMarkets.find((m) => m.name === item.name);

      return {
        id: item.id,
        name: item.name,
        serviceScope: existing?.serviceScope ?? defaultScope,
      };
    });

    setSelectedMarkets(updatedMarkets);
    nextStep();
  };

  return (
    <div className="w-full max-w-[686px]">
      <div className="common-title pb-[12px]">Pick Markets</div>
      <div className="common-sub-text pb-[60px]">
        Select the markets you’re ready to support with your services.
        {validationError && (
          <div className="text-red-500 mt-2 text-sm">{validationError}</div>
        )}
      </div>

      <div className="flex flex-col gap-y-[32px]">
        {allMarkets.length > 0 ? (
          <MarketSelection
            label=""
            checked={true}
            showCheckBox={false}
            options={allMarkets}
            selected={
              Object.keys(data.markets)
                .map((name) => {
                  const matched = allMarkets.find((m) => m.name === name);
                  if (!matched) return null; // fallback for unmatched markets
                  return {
                    id: matched.id,
                    name: matched.name,
                  };
                })
                .filter((item): item is OptionProps => !!item) // remove nulls
            }
            setSelected={(selected) => {
              setLocalMarkets(selected);
              setData((current) => {
                const updatedMarkets: {
                  [marketCode: string]: PartnerMarketType;
                } = {};

                // Add new selected markets (if not already present)
                selected.forEach((market) => {
                  if (!updatedMarkets[market.name]) {
                    updatedMarkets[market.name] = {
                      focus_categories: {},
                      region: 0,
                      channel_coverage: [],
                      service_capabilities: [],
                      warehousing: [],
                      engagement_model: [],
                      trading_terms: "",
                      minimum_tenure: "",
                      trade_marketing_investments: "",
                      payment_terms: [],
                    };
                  }
                });

                return {
                  ...current,
                  markets: updatedMarkets,
                };
              });
            }}
            setChecked={() => {}}
            otherInputValue={otherInputValue}
            setOtherInputValue={setOtherInputValue}
          />
        ) : (
          <div className="h-40"></div>
        )}
      </div>

      <div
        className={` mt-[60px] inline-flex flex justify-end  gap-x-[8px] w-full md:w-auto hidden md:block`}
      >
        {
          <NavigationButton
            onClick={previousStep}
            label="Previous"
            type="text"
          />
        }
        <NavigationButton onClick={handleNext} label={"Next"} type="primary" />
      </div>
      <div className="fixed bottom-0 left-0 right-0 flex justify-center px-4 py-4 gap-x-2 w-full w-full md:w-auto shadow-lg z-20 bg-white block md:hidden">
        {
          <NavigationButton
            onClick={previousStep}
            label="Previous"
            type="text"
            block={true}
          />
        }
        <NavigationButton
          onClick={handleNext}
          label={`Next`}
          type="primary"
          block={true}
        />
      </div>
    </div>
  );
};

export default PickMarketsPage;
